import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, ShieldCheck, Loader2, Globe, Copy, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

export const SOCIETE_ROLE_LABELS = {
  admin: { label: "Admin société", color: "#f7c935" },
  member: { label: "Membre", color: "#3b82f6" },
  readonly: { label: "Lecture seule", color: "#94a3b8" },
  none: { label: "Aucun accès", color: "#64748b" },
};
const SEL = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs";

function TriCell({ base, override, onChange, testId }) {
  const forced = override !== undefined && override !== null;
  const effective = forced ? override : !!base;
  const next = () => onChange(!forced ? !base : override === true ? false : null);
  return (
    <button onClick={next} data-testid={testId} title={forced ? (override ? "Forcé : autorisé" : "Forcé : interdit") : `Hérité du poste/rôle : ${base ? "autorisé" : "interdit"}`}
      className={`w-7 h-7 rounded-md text-[10px] font-black transition-colors ${effective ? "bg-emerald-500/25 text-emerald-300" : "bg-white/5 text-slate-600"} ${forced ? "ring-2 ring-[#f7c935]" : ""}`}>
      {effective ? "✓" : "–"}
    </button>
  );
}

function SocietesTab({ api, access, reload, currentUser }) {
  const [saving, setSaving] = useState("");
  const setRole = async (sid, role) => {
    setSaving(sid);
    try { await api.put(`/admin/staff/${access.user_id}/access/societes/${sid}`, { role }); toast.success("Rôle mis à jour"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(""); }
  };
  const toggleGroup = async () => {
    setSaving("group");
    try { await api.put(`/admin/staff/${access.user_id}/group-access`, { enabled: !access.group_access }); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(""); }
  };
  return (
    <div className="space-y-3" data-testid="member-access-societes">
      {access.role === "superadmin" ? (
        <div className="text-xs text-[#f7c935] bg-[#f7c935]/10 rounded-lg p-3 flex items-center gap-2"><Globe className="w-4 h-4" /> Direction du groupe : accès à toutes les sociétés et à la vue groupe.</div>
      ) : (
        <div className={`flex items-center justify-between rounded-lg p-3 border ${access.group_access ? "border-[#f7c935]/40 bg-[#f7c935]/5" : "border-white/10 bg-[#0b0811]"}`}>
          <div>
            <div className="text-xs font-bold text-slate-200 flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Accès toutes sociétés (membre transverse)</div>
            <div className="text-[11px] text-slate-500">Compta, RH… — équivaut à « Admin société » partout. Réservé à la direction du groupe.</div>
          </div>
          <button onClick={toggleGroup} disabled={!access.group_access_editable || saving === "group"} className={`w-11 h-6 rounded-full relative transition-colors disabled:opacity-40 ${access.group_access ? "bg-[#f7c935]" : "bg-white/15"}`} data-testid="member-group-access-toggle">
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${access.group_access ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      )}
      <div className="rounded-lg border border-white/10 divide-y divide-white/5">
        {access.societes.map((s) => {
          const info = SOCIETE_ROLE_LABELS[s.role] || SOCIETE_ROLE_LABELS.none;
          const locked = access.group_access || access.role === "superadmin" || !s.can_manage || access.user_id === currentUser?.user_id;
          return (
            <div key={s.societe_id} className="flex items-center justify-between px-3 py-2" data-testid={`member-societe-${s.societe_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color || "#64748b" }} />
                <span className="text-sm text-slate-200 truncate">{s.name}</span>
                {!s.can_manage && <span className="text-[10px] text-slate-600">(hors de votre périmètre)</span>}
              </div>
              {locked ? (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: info.color, background: `${info.color}1a` }}>{info.label}</span>
              ) : (
                <select value={s.role} disabled={saving === s.societe_id} onChange={(e) => setRole(s.societe_id, e.target.value)} className={SEL} data-testid={`member-societe-role-${s.societe_id}`}>
                  {Object.entries(SOCIETE_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">Un membre ne voit que les sociétés où il a un rôle. « Lecture seule » bloque toute modification ; « Admin société » peut gérer l'équipe et les autorisations de cette société.</p>
    </div>
  );
}

function PermissionsTab({ api, access }) {
  const manageable = access.societes.filter((s) => s.can_manage && (access.group_access || access.role === "superadmin" || s.role !== "none"));
  const [sid, setSid] = useState(manageable[0]?.societe_id || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [module, setModule] = useState("");
  const [copyTo, setCopyTo] = useState("");
  const [postes, setPostes] = useState([]);
  const [posteId, setPosteId] = useState(access.poste_id || "");

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const [m, p] = await Promise.all([api.get("/societe-permissions/matrix", { params: { societe_id: sid } }), api.get("/tenant/postes").catch(() => ({ data: [] }))]);
      setData(m.data); setPostes(Array.isArray(p.data) ? p.data : p.data?.items || p.data?.postes || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Chargement impossible"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, access.user_id]);
  useEffect(() => { load(); }, [load]);

  const me = useMemo(() => (data?.members || []).find((x) => x.user_id === access.user_id), [data, access.user_id]);
  const modules = useMemo(() => { const m = {}; (data?.catalog || []).forEach((c) => { m[c.module] = c.module_label; }); return Object.entries(m); }, [data]);
  const cols = useMemo(() => (data?.catalog || []).filter((c) => !module || c.module === module), [data, module]);

  const setOverride = async (key, value) => {
    try {
      const r = await api.put("/societe-permissions/override", { societe_id: sid, scope: "user", ref_id: access.user_id, key, value });
      setData((d) => ({ ...d, members: d.members.map((x) => (x.user_id === access.user_id ? { ...x, overrides: r.data.overrides } : x)) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const savePoste = async (v) => {
    setPosteId(v);
    try { await api.put(`/admin/staff/${access.user_id}`, { poste_id: v || null }); toast.success("Poste mis à jour"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const copy = async () => {
    if (!copyTo) return;
    try { const r = await api.post("/societe-permissions/copy", { from_societe_id: sid, to_societe_id: copyTo, user_id: access.user_id }); toast.success(`${r.data.copied} réglage(s) copié(s)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!manageable.length) return <div className="text-sm text-slate-500 p-4" data-testid="member-perms-empty">Autorisez d'abord ce membre sur une société que vous administrez.</div>;
  return (
    <div className="space-y-3" data-testid="member-access-permissions">
      <div className="flex flex-wrap items-center gap-2">
        <select value={sid} onChange={(e) => setSid(e.target.value)} className={SEL} data-testid="member-perms-societe">
          {manageable.map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-400"><Briefcase className="w-3.5 h-3.5" />
          <select value={posteId} onChange={(e) => savePoste(e.target.value)} className={SEL} data-testid="member-perms-poste">
            <option value="">Sans poste</option>
            {postes.map((p) => <option key={p.poste_id} value={p.poste_id}>{p.name || p.label}</option>)}
          </select>
        </label>
        <select value={module} onChange={(e) => setModule(e.target.value)} className={SEL} data-testid="member-perms-module">
          <option value="">Tous les modules</option>
          {modules.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <select value={copyTo} onChange={(e) => setCopyTo(e.target.value)} className={SEL} data-testid="member-perms-copy-to">
            <option value="">Copier vers…</option>
            {manageable.filter((s) => s.societe_id !== sid).map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={copy} disabled={!copyTo} className="h-7 text-xs border-white/10" data-testid="member-perms-copy-btn"><Copy className="w-3 h-3 mr-1" /> Copier</Button>
        </div>
      </div>
      {loading || !me ? (
        <div className="py-8 text-center text-slate-500 text-sm">{loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ce membre n'apparaît pas encore dans cette société."}</div>
      ) : (
        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5" data-testid="member-perms-list">
          {cols.map((c) => (
            <div key={c.key} className="flex items-center justify-between px-3 py-1.5 gap-3">
              <div className="min-w-0">
                <div className="text-xs text-slate-200 truncate">{c.label}</div>
                <div className="text-[10px] text-slate-600">{c.module_label} · {c.key}</div>
              </div>
              <TriCell base={me.base?.[c.key]} override={me.overrides?.[c.key]} onChange={(v) => setOverride(c.key, v)} testId={`member-perm-${c.key}`} />
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-500">✓ vert = autorisé, – = interdit ; contour jaune = forcé pour cette société (sinon hérité du poste et du rôle). Appliqué côté serveur et dans le menu pour la société active.</p>
    </div>
  );
}

export default function MemberAccessDialog({ open, onClose, api, member, currentUser, onChanged }) {
  const [tab, setTab] = useState("societes");
  const [access, setAccess] = useState(null);
  const reload = useCallback(async () => {
    if (!member?.user_id) return;
    try { const r = await api.get(`/admin/staff/${member.user_id}/access`); setAccess(r.data); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Chargement impossible"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.user_id]);
  useEffect(() => {
    if (open) { setTab("societes"); setAccess(null); reload(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.user_id]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl" data-testid="member-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#f7c935]" /> Accès & autorisations — {member?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 bg-[#0b0811] border border-white/5 rounded-lg p-1 w-fit">
          {[["societes", "Sociétés & rôles", Building2], ["permissions", "Autorisations par société", ShieldCheck]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 ${tab === k ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white"}`} data-testid={`member-access-tab-${k}`}><I className="w-3.5 h-3.5" />{l}</button>
          ))}
        </div>
        {!access ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
          : tab === "societes" ? <SocietesTab api={api} access={access} reload={reload} currentUser={currentUser} />
          : <PermissionsTab api={api} access={access} />}
      </DialogContent>
    </Dialog>
  );
}
